import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import {
  ControlledComboBox,
  ControlledInput,
} from "@excent-one/react/form/inputs";
import { hyperlinkOpenIcon, plusIcon } from "@progress/kendo-svg-icons";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
  ContentHeaderActions,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { FormFieldSet } from "@progress/kendo-react-form";
import { SelectionRange } from "@progress/kendo-react-all";
import { useAuthContext } from "@excent-one/auth";
import {
  createChangeRequestByDateRange,
  getAllAreas,
  getAllChangeRequestStatuses,
  getCoordinators,
  getMyDetails,
  getMyMembers,
  searchSscrSiteAssignments,
  SscrSiteAssignmentSummaryDto,
} from "@phrmpc/infrastructure/phrmpc";
import {
  FilterLogic,
  FilterOperator,
  MONTHS_OPTIONS,
  YEARS_OPTIONS,
} from "@phrmpc/shared";
import { useQuery } from "@tanstack/react-query";
import { notification } from "@excent-one/react/notification";
import { DateRangePickerButton } from "../components";
import { DateTime } from "luxon";
import { isEmpty, isNil } from "lodash";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";
import { getServerErrorMessage } from "../utils/ErrorMessages";

interface SearchFormValues {
  monthId: number | null;
  year: number | null;
  coordinatorId: string;
  coordinatorName: string;
  areaId: number | null;
  statusId: number | null;
}

const buildFilter = (values: SearchFormValues) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];

  if (values.monthId) {
    filters.push({
      field: "monthId",
      operator: FilterOperator.Equals,
      value: values.monthId,
    });
  }
  if (values.year) {
    filters.push({
      field: "year",
      operator: FilterOperator.Equals,
      value: values.year,
    });
  }
  if (!isEmpty(values.coordinatorId)) {
    filters.push({
      field: "coordinatorId",
      operator: FilterOperator.Equals,
      value: values.coordinatorId,
    });
  }
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }
  if (values.areaId != null) {
    filters.push({
      field: "areaId",
      operator: FilterOperator.Equals,
      value: values.areaId,
    });
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};

const ScheduleAndSiteChangeRequestSearchForm = () => {
  const navigate = useNavigate();
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();

  const isUserCoordinator = isInAnyRole("Coordinator");
  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserManager = isInAnyRole("Manager");

  const { data: statusOptions } = useQuery({
    queryKey: ["schedule-site-change-request-statuses"],
    queryFn: () => getAllChangeRequestStatuses(),
  });

  const { data: areaOptions } = useQuery({
    queryKey: ["areas"],
    queryFn: () => getAllAreas(),
  });

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { data: memberCoordinators } = useQuery({
    queryKey: ["my-members", currentUser?.id],
    queryFn: () => getMyMembers(),
  });

  const { data: allCoordinatorsAndAreaHeads } = useQuery({
    queryKey: ["all-coordinators", currentUser?.id],
    queryFn: () =>
      getCoordinators({
        payload: {
          pageSize: 10000,
        },
      } as any),
  });

  const coordinatorOptions = useMemo(() => {
    const members = isUserManager
      ? (allCoordinatorsAndAreaHeads?.items ?? [])
      : isUserAreaHead
        ? (memberCoordinators?.items ?? [])
        : [];
    if (members.length === 0 || isUserCoordinator) {
      return [];
    }

    if (!myDetails) {
      return members;
    }
    return [
      {
        id: myDetails.id,
        fieldName: myDetails.fieldName,
      },
      ...members,
    ];
  }, [
    memberCoordinators?.items,
    allCoordinatorsAndAreaHeads?.items,
    myDetails,
  ]);

  const defaultSearchValues: SearchFormValues = {
    monthId: null,
    year: null,
    coordinatorId: myDetails?.id ?? "",
    coordinatorName: myDetails?.fieldName ?? "",
    areaId: null,
    statusId: null,
  };

  const { control, getValues, reset } = useForm<SearchFormValues>({
    defaultValues: defaultSearchValues,
  });

  useEffect(() => {
    if (!myDetails) {
      return;
    }

    const coordinatorId = isUserAreaHead
      ? (coordinatorOptions?.[0]?.id ?? "")
      : (myDetails?.id ?? "");

    const coordinatorName = isUserAreaHead
      ? (coordinatorOptions?.[0]?.fieldName ?? "")
      : (myDetails?.fieldName ?? "");

    const initialSearchValues: SearchFormValues = {
      monthId: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      coordinatorId,
      coordinatorName,
      areaId: null,
      statusId: null,
    };
    reset(initialSearchValues);
    handleSearch();
  }, [myDetails, coordinatorOptions, reset]);

  const [appliedFilters, setAppliedFilters] =
    useState<SearchFormValues>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  const {
    data: pagedResult,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["schedule-site-change-requests", appliedFilters, skip, pageSize],
    queryFn: () =>
      searchSscrSiteAssignments({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters) as any,
        },
      } as any),
    refetchOnMount: "always",
    staleTime: 0,
  });

  const isSearchCriteriaEmpty = (values: SearchFormValues) =>
    !values.monthId &&
    !values.year &&
    (isUserCoordinator || isEmpty(values.coordinatorId)) &&
    values.areaId == null &&
    values.statusId == null;

  const handleSearch = () => {
    const values = getValues();
    if (isSearchCriteriaEmpty(values)) {
      notification.error("Please specify at least one search criterion.");
      return;
    }
    resetPaging();
    setAppliedFilters(values);
    refetch();
  };

  const handleClear = () => {
    const coordinatorId =
      coordinatorOptions.length > 0 ? "" : (myDetails?.id ?? "");
    const coordinatorName =
      coordinatorOptions.length > 0 ? "" : (myDetails?.fieldName ?? "");
    const clearSearchValues: SearchFormValues = {
      monthId: null,
      year: null,
      coordinatorId,
      coordinatorName,
      areaId: null,
      statusId: null,
    };

    reset(clearSearchValues);
    resetPaging();
    setAppliedFilters(clearSearchValues);
    refetch();
  };

  const gridData = (pagedResult?.items ?? []).map((row) => ({
    ...row,
  }));

  const COLUMNS: DataGridColumnProps<SscrSiteAssignmentSummaryDto>[] = [
    { field: "id", title: "MCP ID", readOnly: true, hidden: true },
    { field: "monthName", title: "Month", readOnly: true },
    {
      field: "dateFrom",
      title: "Date From",
      readOnly: true,
      cells: {
        data: (props: any) => (
          <td>
            {props.dataItem.dateFrom
              ? DateTime.fromJSDate(props.dataItem.dateFrom).toFormat("MM/dd/yyyy")
              : "-"}
          </td>
        ),
      },
    },
    {
      field: "dateTo",
      title: "Date To",
      readOnly: true,
      cells: {
        data: (props: any) => (
          <td>
            {props.dataItem.dateTo
              ? DateTime.fromJSDate(props.dataItem.dateTo).toFormat("MM/dd/yyyy")
              : "-"}
          </td>
        ),
      },
    },
    { field: "statusName", title: "Status", readOnly: true },
  ];

  const handleCreate = async ({ start, end }: SelectionRange) => {
    if (!currentUser?.id || !myDetails) {
      notification.error("Unable to determine current user. Please try again.");
      return;
    }
    try {
      if (isNil(start) || isNil(end)) {
        notification.error("Start date and/or end date is not specified.");
        return;
      }

      const created = await createChangeRequestByDateRange({
        from: start,
        to: end,
      });

      navigate({
        to: "/deployment/schedule-change/$id/edit",
        params: {
          id: created.id!,
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
      notification.error(
        getServerErrorMessage(
          e,
          "Unable to create site change request. Please try again.",
        ),
      );
    }
  };

  const today = DateTime.now();
  const weekFromNow = today.plus({ days: 6 });
  const lastDayOfTheMonth = today.endOf("month");
  const effectiveMaxDate = DateTime.min(weekFromNow, lastDayOfTheMonth);

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Schedule and Site Change Requests" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          withAdvancedFilters
          buttonProps={{
            search: { type: "submit", onClick: handleSearch },
            clear: { type: "button", onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledComboBox
              control={control}
              name="monthId"
              label="Month"
              data={MONTHS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: "basic-search-field" }}
            />
            <ControlledComboBox
              control={control}
              name="year"
              label="Year"
              data={YEARS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: "basic-search-field" }}
            />
            {coordinatorOptions.length ? (
              <ControlledComboBox
                control={control}
                name="coordinatorId"
                label="Coordinator"
                data={coordinatorOptions}
                dataItemKey="id"
                textField="fieldName"
                wrapperProps={{ className: "basic-search-field" }}
              />
            ) : (
              <ControlledInput
                control={control}
                name="coordinatorName"
                label="Coordinator Name"
                placeholder="Search by coordinator name"
                wrapperProps={{ className: "basic-search-field" }}
                readOnly
              />
            )}
            {areaOptions && (
              <ControlledComboBox
                control={control}
                name="areaId"
                label="Area"
                data={areaOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
            {statusOptions && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Status"
                data={statusOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
        <ContentHeaderActions>
          {!isUserManager && (
            <DateRangePickerButton
              buttonProps={{
                type: "button",
                themeColor: "primary",
                svgIcon: plusIcon,
              }}
              dateRangeProps={{
                min: today.toJSDate(),
                max: effectiveMaxDate.toJSDate(),
              }}
              onConfirm={handleCreate}
            >
              Create Schedule/Site Change Request
            </DateRangePickerButton>
          )}
        </ContentHeaderActions>
      </ContentHeader>
      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            data={gridData}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching schedule and site change requests found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
            commands={[
              {
                command: "view",
                text: "View",
                svgIcon: hyperlinkOpenIcon,
              },
            ]}
            onCommandClicked={{
              view: (e) => {
                if (e.dataItem == null) return;
                navigate({ to: `${e.dataItem.id}/display` });
              },
            }}
          />
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default ScheduleAndSiteChangeRequestSearchForm;
